import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AssignmentOutlined as AssignmentIcon,
  CallEnd as CallEndIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  GraphicEq as GraphicEqIcon,
  LightbulbOutlined as LightbulbIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  PlayArrow as PlayArrowIcon,
  Replay as ReplayIcon,
  Subject as TranscriptIcon,
  WorkOutline as WorkIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import { API_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { LiveStt } from '../lib/liveStt';
import { EnglishReviewContent } from './EnglishReview';


const now = () => new Date().toISOString();

const makeMessage = (role, text) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  role,
  text,
  timestamp: now(),
});

const formatTime = (seconds) => (
  `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
);

const speechRates = { A2: 0.85, B1: 0.95, B2: 1 };

// Human-written English copy for scenario fields the API only returns in Chinese (keyed by scenario id).
const ENGLISH_SCENARIO_COPY = {
  'english-app-launch': {
    title: 'Global App Launch Status Meeting',
  },
};

const localizedScenarioTitle = (scenario, isEnglish) => (
  isEnglish ? ENGLISH_SCENARIO_COPY[scenario.id]?.title || scenario.title : scenario.title
);

// LiveStt reports its connection errors in Chinese; these keys match its messages exactly.
const ENGLISH_STT_ERRORS = {
  '無法取得即時語音辨識權限': 'Unable to access live speech recognition.',
  '即時語音連線發生錯誤': 'The live voice connection ran into an error.',
  '即時語音連線已中斷': 'The live voice connection was interrupted.',
};

const CJK_TEXT = new RegExp('[\\u3400-\\u9fff\\uff00-\\uffef\\u3000-\\u303f]');

const englishSttError = (message, fallback) => (
  ENGLISH_STT_ERRORS[message] || (!message || CJK_TEXT.test(message) ? fallback : message)
);

function Waveform({ active, color = '#1976d2' }) {
  const [levels, setLevels] = useState(() => Array(24).fill(0.12));

  useEffect(() => {
    const interval = setInterval(() => {
      setLevels((previous) => previous.map((_, index) => {
        if (!active) return 0.1;
        const center = 1 - Math.abs(index - 12) / 18;
        return Math.max(0.08, (0.22 + Math.random() * 0.78) * center);
      }));
    }, 110);
    return () => clearInterval(interval);
  }, [active]);

  return (
    <Box sx={{ height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
      {levels.map((level, index) => (
        <Box
          key={index}
          sx={{
            width: 3,
            height: Math.max(3, level * 34),
            borderRadius: 3,
            bgcolor: color,
            opacity: 0.3 + level * 0.7,
            transition: 'height 110ms linear, opacity 110ms linear',
          }}
        />
      ))}
    </Box>
  );
}

function CallControl({ label, children, onClick, disabled, primary, danger }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      <IconButton
        onClick={onClick}
        disabled={disabled}
        sx={{
          width: primary ? 72 : 56,
          height: primary ? 72 : 56,
          bgcolor: danger ? '#d32f2f' : primary ? '#16855b' : '#fff',
          color: danger || primary ? '#fff' : '#1f2937',
          boxShadow: '0 8px 28px rgba(15,23,42,.12)',
          border: danger || primary ? 'none' : '1px solid rgba(15,23,42,.08)',
          '&:hover': { bgcolor: danger ? '#b71c1c' : primary ? '#106b49' : '#f5f7fb' },
          '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' },
        }}
      >
        {children}
      </IconButton>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}

function EnglishPracticeSummary({
  scenario,
  level,
  elapsed,
  messages,
  completedTasks,
  review,
  loading,
  error,
  onRetry,
  onExit,
}) {
  const { tr, isEnglish } = useLanguage();
  const learnerTurns = messages.filter((item) => item.role === 'learner').length;
  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1500, overflowY: 'auto', bgcolor: '#f4f7fb' }}>
      <Box sx={{ maxWidth: 980, mx: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="overline" color="primary" fontWeight={900}>{tr('英文口說練習完成', 'English Speaking Practice Complete')}</Typography>
            <Typography variant="h4" fontWeight={900}>{localizedScenarioTitle(scenario, isEnglish)}</Typography>
          </Box>
          <IconButton onClick={onExit} aria-label={tr('關閉', 'Close')}><CloseIcon /></IconButton>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label={`CEFR ${level}`} color="primary" />
          <Chip label={tr(`練習時間 ${formatTime(elapsed)}`, `Practice time ${formatTime(elapsed)}`)} />
          <Chip label={tr(`學員發言 ${learnerTurns} 次`, `Your turns: ${learnerTurns}`)} />
          <Chip label={tr(`完成任務 ${completedTasks.size}/${scenario.tasks.length}`, `Tasks completed ${completedTasks.size}/${scenario.tasks.length}`)} />
        </Box>

        {loading ? (
          <Paper sx={{ p: 8, borderRadius: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }} fontWeight={800}>{tr('Sonnet 5 正在產生英文講評…', 'Sonnet 5 is writing your English review…')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{tr('逐字稿已先儲存到練習紀錄', 'Your transcript has already been saved to your practice history')}</Typography>
          </Paper>
        ) : (
          <>
            {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
            {review ? <EnglishReviewContent review={review} /> : (
              <Paper sx={{ p: 4, borderRadius: 4 }}><Typography>{tr('講評暫時無法產生，逐字稿仍已保存。', 'The review is not available right now, but your transcript has been saved.')}</Typography></Paper>
            )}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant="outlined" size="large" startIcon={<ReplayIcon />} onClick={onRetry}>{tr('再練一次', 'Practice Again')}</Button>
              <Button variant="contained" size="large" onClick={onExit}>{tr('返回場景', 'Back to Scenario')}</Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

function EnglishLiveSession({ scenario, level, onExit, onRetry }) {
  const { tr, isEnglish, language } = useLanguage();
  const [messages, setMessages] = useState(() => [makeMessage('partner', scenario.opening_line)]);
  const [status, setStatus] = useState('connecting');
  const [elapsed, setElapsed] = useState(0);
  const [partial, setPartial] = useState('');
  const [typing, setTyping] = useState(false);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [micMode, setMicMode] = useState('open');
  const [holding, setHolding] = useState(false);
  const [hint, setHint] = useState(null);
  const [hintLoading, setHintLoading] = useState(false);
  const [completedTasks, setCompletedTasks] = useState(new Set());
  const [showTasks, setShowTasks] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [review, setReview] = useState(null);

  const engineRef = useRef(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef('');
  const messagesRef = useRef(messages);
  const typingRef = useRef(false);
  const pausedRef = useRef(false);
  const micModeRef = useRef('open');
  const speechStartedRef = useRef(0);
  const startedAtRef = useRef(Date.now());
  const finishedAtRef = useRef(null);
  const finishStartedRef = useRef(false);
  const conversationIdRef = useRef(Date.now());
  const sendUtteranceRef = useRef(() => {});
  const playSpeechRef = useRef(() => Promise.resolve());
  const finishRef = useRef(() => {});
  const conversationScrollRef = useRef(null);
  const trRef = useRef(tr);
  trRef.current = tr;

  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => {
    const conversation = conversationScrollRef.current;
    if (conversation) conversation.scrollTop = conversation.scrollHeight;
  }, [messages, partial]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = '';
    }
    window.speechSynthesis?.cancel();
    setAiSpeaking(false);
  };

  const playSpeech = async (text, afterPlayback) => {
    stopAudio();
    try {
      const response = await fetch(`${API_URL}/api/english-practice/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario_id: scenario.id, level, text }),
      });
      if (!response.ok) throw new Error('tts unavailable');
      const url = URL.createObjectURL(await response.blob());
      audioUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      speechStartedRef.current = Date.now();
      setAiSpeaking(true);
      await new Promise((resolve) => {
        const finish = () => {
          setAiSpeaking(false);
          if (audioUrlRef.current === url) {
            URL.revokeObjectURL(url);
            audioUrlRef.current = '';
          }
          resolve();
        };
        audio.onended = finish;
        audio.onerror = finish;
        audio.onpause = finish;
        audio.play().catch(finish);
      });
    } catch (speechError) {
      if ('speechSynthesis' in window) {
        await new Promise((resolve) => {
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.lang = 'en-US';
          utterance.rate = speechRates[level];
          utterance.onend = resolve;
          utterance.onerror = resolve;
          setAiSpeaking(true);
          window.speechSynthesis.speak(utterance);
        });
        setAiSpeaking(false);
      }
    }
    afterPlayback?.();
  };
  playSpeechRef.current = playSpeech;

  const appendMessages = (nextMessages) => {
    messagesRef.current = nextMessages;
    setMessages(nextMessages);
  };

  const sendUtterance = async (text) => {
    const cleaned = text.trim();
    if (!cleaned || typingRef.current || summary) return;
    typingRef.current = true;
    setTyping(true);
    setPartial('');
    engineRef.current?.setPaused(true);
    const learnerMessage = makeMessage('learner', cleaned);
    let withLearner = [...messagesRef.current, learnerMessage];
    appendMessages(withLearner);

    try {
      const response = await axios.post(`${API_URL}/api/english-practice/respond`, {
        scenario_id: scenario.id,
        level,
        messages: withLearner.map(({ role, text: messageText }) => ({ role, text: messageText })),
      });
      if (response.data.polished_learner_text) {
        withLearner = withLearner.map((message) => (
          message.id === learnerMessage.id
            ? { ...message, text: response.data.polished_learner_text }
            : message
        ));
      }
      const partnerMessage = makeMessage('partner', response.data.reply);
      appendMessages([...withLearner, partnerMessage]);
      setHint(null);
      setCompletedTasks(new Set(response.data.completed_task_ids || []));
      typingRef.current = false;
      setTyping(false);
      if (!pausedRef.current && micModeRef.current === 'open') engineRef.current?.setPaused(false);
      await playSpeechRef.current(response.data.reply, () => {
        if (response.data.should_end) setTimeout(() => finishRef.current(), 500);
      });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || tr('Alex 暫時無法回應，請再說一次。', "Alex couldn't respond just now. Please say that again."));
    } finally {
      typingRef.current = false;
      setTyping(false);
      if (!pausedRef.current && micModeRef.current === 'open') engineRef.current?.setPaused(false);
    }
  };
  sendUtteranceRef.current = sendUtterance;

  useEffect(() => {
    const timer = setInterval(() => {
      if (finishedAtRef.current === null) {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }
    }, 1000);
    const engine = new LiveStt({
      tokenUrl: '/api/english-practice/stt-token',
      languageHints: ['en'],
      onReady: () => {
        setStatus('connected');
        playSpeechRef.current(scenario.opening_line);
      },
      onUtterance: (text) => sendUtteranceRef.current(text),
      onPartial: setPartial,
      onSpeaking: () => {
        if (audioRef.current && !audioRef.current.paused && Date.now() - speechStartedRef.current > 500) {
          audioRef.current.pause();
          setAiSpeaking(false);
        }
      },
      onError: (message) => {
        setError(trRef.current(message, englishSttError(message, 'The live voice connection ran into an error.')));
        setStatus('error');
      },
    });
    engineRef.current = engine;
    engine.start().catch((startError) => {
      setStatus('error');
      setError(trRef.current(
        startError.message || '無法啟動麥克風，請確認瀏覽器權限。',
        englishSttError(startError.message, 'Unable to start the microphone. Please check your browser permissions.'),
      ));
    });

    return () => {
      clearInterval(timer);
      engine.stop();
      engineRef.current = null;
      stopAudio();
    };
    // A remounted session owns one speech engine for its full lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario.id, level]);

  const fetchHint = async () => {
    if (hintLoading) return;
    setHintLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/english-practice/hint`, {
        scenario_id: scenario.id,
        level,
        language,
        messages: messagesRef.current.map(({ role, text }) => ({ role, text })),
      });
      setHint(response.data);
    } catch (hintError) {
      setError(tr('暫時無法取得英文提示。', 'Unable to get an English hint right now.'));
    } finally {
      setHintLoading(false);
    }
  };

  const finishPractice = async () => {
    if (finishStartedRef.current) return;
    finishStartedRef.current = true;
    finishedAtRef.current = Date.now();
    const finalMessages = messagesRef.current;
    const practiceTime = Math.max(1, Math.floor((finishedAtRef.current - startedAtRef.current) / 1000));
    setElapsed(practiceTime);
    engineRef.current?.stop();
    engineRef.current = null;
    stopAudio();
    setStatus('ended');
    setSummary(true);
    setSummaryLoading(true);

    const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
    const storageMessages = finalMessages.map((message) => ({
      text: message.text,
      sender: message.role === 'learner' ? 'user' : 'bot',
      role: message.role,
      language: 'en',
      timestamp: message.timestamp,
    }));

    try {
      await axios.put(`${API_URL}/api/storage/conversations/${conversationIdRef.current}`, {
        member_id: storedUser?.id || null,
        scenario_id: scenario.id,
        scenario_title: `[ENGLISH] ${scenario.title} · ${level}`,
        timestamp: now(),
        messages: storageMessages,
      });
    } catch (storageError) {
      console.error('Unable to save English practice transcript', storageError);
    }

    try {
      const response = await axios.post(`${API_URL}/api/english-practice/review`, {
        scenario_id: scenario.id,
        level,
        language,
        messages: finalMessages.map(({ role, text }) => ({ role, text })),
      });
      const nextReview = response.data;
      setReview(nextReview);
      const completed = new Set(nextReview.completed_task_ids || []);
      setCompletedTasks(completed);

      try {
        await axios.put(`${API_URL}/api/storage/conversations/${conversationIdRef.current}/analysis`, {
          member_id: storedUser?.id || null,
          analysis_data: nextReview,
          total_score: nextReview.total_score || 0,
          completed_checkpoints: completed.size,
          incomplete_checkpoints: Math.max(0, scenario.tasks.length - completed.size),
          completion_rate: nextReview.completion_rate || 0,
          practice_time: practiceTime,
          nodes_data: scenario.tasks.map((task) => ({
            ...task,
            completed: completed.has(task.id),
          })),
        });
      } catch (storageError) {
        console.error('Unable to save English practice review', storageError);
      }
    } catch (reviewError) {
      setSummaryError(reviewError.response?.data?.detail || tr('英文講評產生失敗，但逐字稿已保存。', 'The English review could not be generated, but your transcript has been saved.'));
    } finally {
      setSummaryLoading(false);
    }
  };
  finishRef.current = finishPractice;

  const togglePause = () => {
    const next = !pausedRef.current;
    pausedRef.current = next;
    setPaused(next);
    engineRef.current?.setPaused(next || micModeRef.current === 'hold');
    if (next) stopAudio();
  };

  const toggleMicMode = () => {
    const next = micModeRef.current === 'open' ? 'hold' : 'open';
    micModeRef.current = next;
    setMicMode(next);
    if (engineRef.current) {
      engineRef.current.manual = next === 'hold';
      engineRef.current.setPaused(next === 'hold' || pausedRef.current);
    }
  };

  const holdStart = () => {
    if (pausedRef.current || status !== 'connected') return;
    setHolding(true);
    engineRef.current?.setPaused(false);
  };

  const holdEnd = () => {
    if (!holding) return;
    setHolding(false);
    engineRef.current?.finalize();
    setTimeout(() => engineRef.current?.setPaused(true), 750);
  };

  if (summary) {
    return (
      <EnglishPracticeSummary
        scenario={scenario}
        level={level}
        elapsed={elapsed}
        messages={messages}
        completedTasks={completedTasks}
        review={review}
        loading={summaryLoading}
        error={summaryError}
        onRetry={onRetry}
        onExit={onExit}
      />
    );
  }

  const visibleMessages = messages.slice(-4);
  const listening = status === 'connected' && !paused && !typing && !aiSpeaking && (micMode === 'open' || holding);
  const statusTitle = status === 'connecting'
    ? tr('正在建立英文語音連線…', 'Connecting to the English voice session…')
    : status === 'error'
      ? tr('即時語音暫時無法使用', 'Live voice is temporarily unavailable')
      : paused
        ? tr('麥克風已靜音', 'Microphone muted')
        : aiSpeaking
          ? 'Alex is speaking'
          : typing
            ? 'Alex is thinking'
            : micMode === 'hold' && !holding
              ? tr('按住麥克風開始說英文', 'Hold the mic button to speak English')
              : 'Your turn';
  const currentTaskIndex = scenario.tasks.findIndex((task) => !completedTasks.has(task.id));
  const currentTask = currentTaskIndex >= 0 ? scenario.tasks[currentTaskIndex] : null;

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1500, bgcolor: '#f6f8fc', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box component="header" sx={{ flex: 'none', px: { xs: 2, md: 4 }, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(15,23,42,.08)', bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: status === 'connected' ? '#16a36a' : status === 'error' ? '#d32f2f' : '#f5a623' }} />
          <Typography variant="body2" color="text.secondary">ENGLISH LIVE</Typography>
        </Box>
        <Typography fontWeight={900} sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatTime(elapsed)}</Typography>
        <Chip label={`CEFR ${level} · Progress meeting`} size="small" />
      </Box>

      <Box sx={{ minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, overflow: 'hidden' }}>
        <Box sx={{ flex: 'none', pt: { xs: 1.5, md: 2 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ position: 'relative', width: { xs: 82, md: 104 }, height: { xs: 82, md: 104 } }}>
            {(aiSpeaking || listening) && (
              <Box sx={{ position: 'absolute', inset: -10, borderRadius: '50%', border: `3px solid ${aiSpeaking ? '#1976d2' : '#16a36a'}`, opacity: 0.3, animation: 'englishPulse 1.5s ease-out infinite', '@keyframes englishPulse': { '0%': { transform: 'scale(.92)', opacity: .5 }, '100%': { transform: 'scale(1.16)', opacity: 0 } } }} />
            )}
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', borderRadius: '50%', fontSize: { xs: 38, md: 48 }, background: 'linear-gradient(135deg,#dbeafe,#bfdbfe)', boxShadow: '0 10px 35px rgba(15,23,42,.18)', filter: paused ? 'grayscale(.8)' : 'none' }}>
              {scenario.emoji}
            </Box>
          </Box>
          <Typography variant="h6" fontWeight={900} sx={{ mt: 1 }}>{scenario.partner.name}</Typography>
          <Typography variant="caption" color="text.secondary">{scenario.partner.role}</Typography>
          <Waveform active={aiSpeaking || listening} color={aiSpeaking ? '#1976d2' : '#16a36a'} />
        </Box>

        {(hint || hintLoading) && (
          <Paper
            elevation={0}
            sx={{ flex: 'none', width: '100%', maxWidth: 760, p: 1.5, mb: 0.5, borderRadius: 3, bgcolor: '#eaf2ff', border: '1px solid #cfe1ff' }}
          >
            <Typography variant="caption" color="primary" fontWeight={900}>💡 You can say</Typography>
            {hintLoading ? <CircularProgress size={18} sx={{ display: 'block', mt: 0.75 }} /> : (
              <Box sx={{ display: { md: 'flex' }, alignItems: 'baseline', gap: 1.5 }}>
                <Typography fontWeight={800} sx={{ mt: 0.25 }}>{hint?.suggestion_en}</Typography>
                <Typography variant="body2" color="text.secondary">{tr(hint?.meaning_zh, hint?.explanation_en)}</Typography>
              </Box>
            )}
          </Paper>
        )}

        <Box ref={conversationScrollRef} sx={{ width: '100%', maxWidth: 760, flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', gap: 1.5, py: 1.5, pr: 0.5 }}>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {visibleMessages.map((message) => (
              message.role === 'learner' ? (
                <Paper
                  key={message.id}
                  elevation={0}
                  sx={{
                    alignSelf: 'flex-end',
                    maxWidth: '88%',
                    px: 2,
                    py: 1.25,
                    borderRadius: '18px 18px 4px 18px',
                    bgcolor: '#eaf2ff',
                    border: '1px solid #cfe1ff',
                  }}
                >
                  <Typography variant="caption" color="primary" fontWeight={900}>You said</Typography>
                  <Typography fontWeight={800} color="#123d78" sx={{ lineHeight: 1.5 }}>{message.text}</Typography>
                </Paper>
              ) : (
                <Box key={message.id} sx={{ alignSelf: 'flex-start', maxWidth: '88%' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={900}>Alex</Typography>
                  <Typography fontWeight={800} color="#153b6b" sx={{ fontSize: { xs: 17, md: 19 }, lineHeight: 1.5 }}>
                    {message.text}
                  </Typography>
                </Box>
              )
            ))}
          </Box>
          {partial && (
            <Paper
              variant="outlined"
              sx={{ alignSelf: 'flex-end', maxWidth: '88%', px: 2, py: 1.25, borderRadius: '18px 18px 4px 18px', borderStyle: 'dashed', borderColor: 'success.main' }}
            >
              <Typography variant="caption" color="success.main" fontWeight={900}>Listening…</Typography>
              <Typography variant="h6" color="primary">{partial}<Box component="span" sx={{ display: 'inline-block', ml: 0.5, width: 2, height: 20, bgcolor: '#16a36a', animation: 'englishBlink 1s step-end infinite', '@keyframes englishBlink': { '50%': { opacity: 0 } } }} /></Typography>
            </Paper>
          )}
          {error && <Alert severity="warning" onClose={() => setError('')} sx={{ alignSelf: 'stretch' }}>{error}</Alert>}
        </Box>

        <Box sx={{ flex: 'none', width: '100%', textAlign: 'center', py: 1, bgcolor: '#f6f8fc', borderTop: '1px solid rgba(15,23,42,.06)', position: 'relative', zIndex: 1 }}>
          <Typography fontWeight={800}>{statusTitle}</Typography>
          <Typography variant="caption" color="text.secondary">
            {listening
              ? tr('請用英文回答，說完後系統會自動送出', 'Answer in English. Your reply is sent automatically when you finish speaking.')
              : aiSpeaking
                ? tr('直接開口即可打斷 Alex', 'Just start speaking to interrupt Alex')
                : typing
                  ? tr('正在準備回應', 'Preparing a response')
                  : tr('你可以查看任務、提示或逐字稿', 'You can check your tasks, get a hint, or view the transcript')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 'none', px: 2, pt: 1.5, bgcolor: 'rgba(255,255,255,.94)' }}>
        <Paper
          component="button"
          type="button"
          onClick={() => setShowTasks(true)}
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: '100%',
            maxWidth: 760,
            mx: 'auto',
            p: 1.5,
            border: '1px solid',
            borderColor: currentTask ? '#cfe1ff' : 'success.light',
            borderRadius: 3,
            bgcolor: currentTask ? '#f5f9ff' : 'rgba(22,163,106,.06)',
            color: 'text.primary',
            textAlign: 'left',
            cursor: 'pointer',
            font: 'inherit',
            '&:hover': { bgcolor: currentTask ? '#eaf2ff' : 'rgba(22,163,106,.1)' },
          }}
        >
          {currentTask ? <AssignmentIcon color="primary" /> : <CheckCircleIcon color="success" />}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="primary" fontWeight={900}>
              {currentTask
                ? tr(`目前任務 ${currentTaskIndex + 1}/${scenario.tasks.length}`, `Current task ${currentTaskIndex + 1}/${scenario.tasks.length}`)
                : tr('全部任務完成', 'All tasks complete')}
            </Typography>
            <Typography fontWeight={900} noWrap>
              {currentTask
                ? tr(currentTask.zh, currentTask.en)
                : tr('做得很好！你已完成本次所有英文任務', 'Great job! You have completed every English task in this session.')}
            </Typography>
            {currentTask && !isEnglish && <Typography variant="body2" color="text.secondary" noWrap>{currentTask.en}</Typography>}
          </Box>
          <Typography variant="caption" color="primary" fontWeight={800} sx={{ flex: 'none' }}>{tr('查看全部', 'View all')}</Typography>
        </Paper>
      </Box>

      <Box sx={{ flex: 'none', px: 2, py: 2, bgcolor: 'rgba(255,255,255,.94)', borderTop: '1px solid rgba(15,23,42,.08)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: 1.5, md: 3 } }}>
        <CallControl label={tr(`全部任務 ${completedTasks.size}/${scenario.tasks.length}`, `Tasks ${completedTasks.size}/${scenario.tasks.length}`)} onClick={() => setShowTasks(true)}><AssignmentIcon /></CallControl>
        <CallControl label={tr('提示', 'Hint')} onClick={fetchHint} disabled={hintLoading}><LightbulbIcon /></CallControl>
        {micMode === 'hold' ? (
          <Box onPointerDown={holdStart} onPointerUp={holdEnd} onPointerLeave={holdEnd} sx={{ touchAction: 'none' }}>
            <CallControl label={holding ? tr('放開送出', 'Release to send') : tr('按住說話', 'Hold to talk')} primary disabled={paused || status !== 'connected'}><MicIcon /></CallControl>
          </Box>
        ) : (
          <CallControl label={paused ? tr('解除靜音', 'Unmute') : tr('麥克風開啟', 'Mic on')} primary onClick={togglePause} disabled={status === 'connecting'}>
            {paused ? <MicOffIcon /> : <MicIcon />}
          </CallControl>
        )}
        <CallControl label={tr('逐字稿', 'Transcript')} onClick={() => setShowTranscript(true)}><TranscriptIcon /></CallControl>
        <Tooltip title={micMode === 'open' ? tr('切換成按住說話', 'Switch to hold-to-talk') : tr('切換成開放麥克風', 'Switch to open mic')}>
          <Box><CallControl label={micMode === 'open' ? tr('按住模式', 'Hold mode') : tr('開放模式', 'Open mic')} onClick={toggleMicMode}><GraphicEqIcon /></CallControl></Box>
        </Tooltip>
        <CallControl label={tr('結束', 'End')} danger onClick={finishPractice}><CallEndIcon /></CallControl>
      </Box>

      <Dialog open={showTasks} onClose={() => setShowTasks(false)} fullWidth maxWidth="sm" sx={{ zIndex: 1600 }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {tr('本次英文任務', 'English Tasks for This Session')}
          <IconButton onClick={() => setShowTasks(false)} aria-label={tr('關閉', 'Close')}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {scenario.tasks.map((task) => {
            const done = completedTasks.has(task.id);
            return (
              <Paper key={task.id} variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 3, borderColor: done ? 'success.light' : 'divider', bgcolor: done ? 'rgba(22,163,106,.06)' : '#fff' }}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {done ? <CheckCircleIcon color="success" /> : <AssignmentIcon color="disabled" />}
                  <Box>
                    <Typography fontWeight={900}>{tr(task.zh, task.en)}</Typography>
                    {!isEnglish && <Typography variant="body2" color="text.secondary">{task.en}</Typography>}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </DialogContent>
      </Dialog>

      <Dialog open={showTranscript} onClose={() => setShowTranscript(false)} fullWidth maxWidth="sm" sx={{ zIndex: 1600 }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          English transcript
          <IconButton onClick={() => setShowTranscript(false)} aria-label={tr('關閉', 'Close')}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {messages.map((message, index) => (
            <Box key={message.id} sx={{ mb: 2 }}>
              {index > 0 && <Divider sx={{ mb: 2 }} />}
              <Typography variant="caption" color={message.role === 'learner' ? 'primary' : 'text.secondary'} fontWeight={900}>
                {message.role === 'learner' ? 'You' : scenario.partner.name}
              </Typography>
              <Typography sx={{ mt: 0.5, lineHeight: 1.7 }}>{message.text}</Typography>
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default function EnglishPractice() {
  const navigate = useNavigate();
  const { tr, isEnglish } = useLanguage();
  const [scenario, setScenario] = useState(null);
  const [level, setLevel] = useState('B1');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [started, setStarted] = useState(false);
  const [sessionKey, setSessionKey] = useState(0);

  useEffect(() => {
    let active = true;
    axios.get(`${API_URL}/api/english-practice/scenarios`)
      .then((response) => {
        if (!active) return;
        const first = response.data?.[0] || null;
        setScenario(first);
        setLevel(first?.default_level || 'B1');
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  if (started && scenario) {
    return (
      <EnglishLiveSession
        key={`${scenario.id}-${level}-${sessionKey}`}
        scenario={scenario}
        level={level}
        onExit={() => setStarted(false)}
        onRetry={() => setSessionKey((current) => current + 1)}
      />
    );
  }

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1450, overflowY: 'auto', bgcolor: '#f4f7fb' }}>
      <Box sx={{ maxWidth: 1080, mx: 'auto', px: { xs: 2, md: 4 }, py: 3, minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box component="header" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <IconButton onClick={() => navigate('/scenarios')} aria-label={tr('返回', 'Back')}><ArrowBackIcon /></IconButton>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={900}>{tr('英文口說', 'English Speaking')}</Typography>
            <Typography variant="caption" color="text.secondary">Business English LIVE</Typography>
          </Box>
          <Box sx={{ width: 40 }} />
        </Box>

        {loading ? (
          <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 4 }}>{tr('無法載入英文口說場景。', 'Unable to load the English speaking scenario.')}</Alert>
        ) : scenario ? (
          <Box sx={{ mt: 4, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '.9fr 1.1fr' }, gap: 3 }}>
            <Paper sx={{ p: { xs: 3, md: 4 }, minHeight: { md: 560 }, borderRadius: 5, color: '#fff', background: 'linear-gradient(150deg,#0f2f5f,#1976d2)', display: 'flex', flexDirection: 'column' }}>
              <Chip label={tr('商務職場英文', 'Business English')} sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(255,255,255,.18)', color: '#fff' }} />
              <Box sx={{ display: 'grid', placeItems: 'center', flex: 1, py: 4 }}>
                <Box sx={{ width: 150, height: 150, display: 'grid', placeItems: 'center', borderRadius: '50%', fontSize: 74, bgcolor: 'rgba(255,255,255,.16)', boxShadow: '0 18px 50px rgba(0,0,0,.18)' }}>{scenario.emoji}</Box>
              </Box>
              <Typography variant="h3" fontWeight={900}>{scenario.partner.name}</Typography>
              <Typography variant="h6" sx={{ opacity: 0.86 }}>{scenario.partner.role}</Typography>
              <Typography variant="body2" sx={{ mt: 2, opacity: 0.76 }}>{tr('你的角色：App 上線專案負責人', 'Your role: App launch project lead')}</Typography>
            </Paper>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="overline" color="primary" fontWeight={900}>{tr('第一個完整場景', 'Featured Scenario')}</Typography>
              <Typography variant="h4" fontWeight={900}>{localizedScenarioTitle(scenario, isEnglish)}</Typography>
              <Typography color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.8 }}>{tr(scenario.scenario_zh, scenario.scenario_en)}</Typography>

              <Typography variant="h6" fontWeight={900} sx={{ mt: 3 }}>{tr('選擇英文程度', 'Choose Your English Level')}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25, mt: 1 }}>
                {scenario.levels.map((item) => (
                  <Paper
                    key={item}
                    component="button"
                    onClick={() => setLevel(item)}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 3, cursor: 'pointer', textAlign: 'center', borderColor: level === item ? 'primary.main' : 'divider', bgcolor: level === item ? '#eaf2ff' : '#fff', color: 'text.primary' }}
                  >
                    <Typography fontWeight={900}>{item}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item === 'A2'
                        ? tr('基礎短句', 'Basic short sentences')
                        : item === 'B1'
                          ? tr('職場中階', 'Intermediate workplace')
                          : tr('自然進階', 'Natural and advanced')}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <Typography variant="h6" fontWeight={900} sx={{ mt: 3 }}>{tr('本次任務', 'Your Tasks')}</Typography>
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {scenario.tasks.map((task) => (
                  <Box key={task.id} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                    <CheckCircleIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
                    <Box>
                      <Typography fontWeight={800}>{tr(task.zh, task.en)}</Typography>
                      {!isEnglish && <Typography variant="body2" color="text.secondary">{task.en}</Typography>}
                    </Box>
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrowIcon />}
                onClick={() => setStarted(true)}
                sx={{ mt: 4, py: 1.6, borderRadius: 99, fontWeight: 900, fontSize: 17 }}
              >
                {tr(`開始 CEFR ${level} 英文會議`, `Start CEFR ${level} English Meeting`)}
              </Button>
              <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1.25 }}>
                <WorkIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5 }} />
                {tr('AI 全程說英文；任務、提示與講評提供繁體中文', 'The AI speaks only English; tasks, hints, and your review are in English too')}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
